import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Twitter, Youtube } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from './Button';

interface FooterProps {
  darkMode?: boolean;
}

export const Footer: React.FC<FooterProps> = ({ darkMode = false }) => {
  const textColor = darkMode ? "text-white" : "text-text-main";
  const hoverColor = "hover:text-primary";
  
  return (
    <footer className={`${darkMode ? 'bg-black border-t border-white/10' : 'bg-white'} pt-20 pb-10 relative overflow-hidden`}>
      <div className="container mx-auto px-4 relative z-10">
        {/* CTA Section */}
        <div 
          className="rounded-[20px] p-12 flex flex-col md:flex-row items-center justify-between mb-20 relative overflow-hidden"
          style={{
            background: 'linear-gradient(88.69deg, #CA1167 -1.27%, #F47749 98.89%)'
          }}
        >
          <h2 
            className="mb-6 md:mb-0 text-center md:text-left"
            style={{
              fontFamily: 'Nanum Myeongjo',
              fontWeight: 700,
              fontSize: '28px', // Mobile
              lineHeight: '34px',
              letterSpacing: '0%',
              color: '#FFFFFF'
            }}
          >
            <span className="md:text-[40px] md:leading-[45px]">Shape the Future of Packaging with Us!</span>
          </h2>
          <Link to="/contact">
            <Button 
              className="rounded-full px-8 py-3 font-bold color-white  transition-transform hover:scale-105"
              style={{
                background: '#FFFFFF',
                color: '#000000'
              }}
            >
              Join the Team
            </Button>
          </Link>
        </div>

        {/* Navigation & Socials */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-20">
          <div className="flex flex-wrap justify-center gap-4 md:gap-8 mb-6 md:mb-0">
            <motion.div whileHover={{ y: -2 }}><Link to="/" className={`cursor-target text-base font-bold ${textColor} ${hoverColor} transition-colors`}>Home</Link></motion.div>
            <motion.div whileHover={{ y: -2 }}><Link to="/about" className={`cursor-target text-base font-bold ${textColor} ${hoverColor} transition-colors`}>About Us</Link></motion.div>
            <motion.div whileHover={{ y: -2 }}><Link to="/products" className={`cursor-target text-base font-bold ${textColor} ${hoverColor} transition-colors`}>Markets & Products</Link></motion.div>
            <motion.div whileHover={{ y: -2 }}><Link to="/technology" className={`cursor-target text-base font-bold ${textColor} ${hoverColor} transition-colors`}>Machinery & Technology</Link></motion.div>
            <motion.div whileHover={{ y: -2 }}><Link to="/contact" className={`cursor-target text-base font-bold ${textColor} ${hoverColor} transition-colors`}>Contact Us</Link></motion.div>
          </div>
          
          <div className={`flex gap-6 ${textColor}`}>
            <motion.a whileHover={{ y: -5, scale: 1.1 }} transition={{ type: "spring", stiffness: 400, damping: 10 }} href="https://facebook.com/printaura" target="_blank" rel="noopener noreferrer" className={`cursor-target ${textColor} ${hoverColor} transition-colors`}><Facebook size={24} /></motion.a>
            <motion.a whileHover={{ y: -5, scale: 1.1 }} transition={{ type: "spring", stiffness: 400, damping: 10 }} href="https://twitter.com/printaura" target="_blank" rel="noopener noreferrer" className={`cursor-target ${textColor} ${hoverColor} transition-colors`}><Twitter size={24} /></motion.a>
            <motion.a whileHover={{ y: -5, scale: 1.1 }} transition={{ type: "spring", stiffness: 400, damping: 10 }} href="https://instagram.com/printaura" target="_blank" rel="noopener noreferrer" className={`cursor-target ${textColor} ${hoverColor} transition-colors`}><Instagram size={24} /></motion.a>
            <motion.a whileHover={{ y: -5, scale: 1.1 }} transition={{ type: "spring", stiffness: 400, damping: 10 }} href="https://youtube.com/printaura" target="_blank" rel="noopener noreferrer" className={`cursor-target ${textColor} ${hoverColor} transition-colors`}><Youtube size={24} /></motion.a>
          </div>
        </div>

        {/* Large PrintAura Watermark */}
        <div className="w-full text-center pointer-events-none select-none overflow-hidden mb-10">
          <h2 
            style={{
              fontFamily: 'Nanum Myeongjo',
              fontWeight: 800,
              fontSize: 'clamp(50px, 15vw, 200px)',
              lineHeight: '1',
              letterSpacing: '0%',
              background: 'linear-gradient(180deg, #FF309D 34.49%, rgba(153, 29, 94, 0) 71.55%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              opacity: 0.1
            }}
          >
            PrintAura
          </h2>
        </div>

        {/* Bottom Footer */}
        <div className={`flex flex-col justify-center items-center text-center border-t ${darkMode ? 'border-white/10' : 'border-gray-100'} pt-8`}>
          <div className={`text-lg font-bold ${textColor}`}>
            PrintAura Packaging Pvt. Ltd. © {new Date().getFullYear()}, All Rights Reserved
          </div>
          <div className={`text-base mt-2 ${textColor} opacity-80`}>
            A division of Shri Shyam Enterprise (SSE)
          </div>
        </div>
      </div>
    </footer>
  );
};
